import { createContext, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext()

export { ThemeContext }

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// 在模組載入時就讀取並應用主題，避免閃爍
const getInitialTheme = () => {
  if (typeof window === 'undefined') return false
  
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    const isDark = savedTheme === 'dark'
    // 立即應用到 DOM
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    return isDark
  }
  
  // 如果沒有保存的主題，使用系統偏好
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  // 立即應用到 DOM
  if (prefersDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  // 保存初始主題
  localStorage.setItem('theme', prefersDark ? 'dark' : 'light')
  return prefersDark
}

export const ThemeProvider = ({ children }) => {
  // 使用函數初始化，確保在首次渲染時就有正確的值
  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme)

  // 當主題改變時，更新 DOM 和 localStorage
  useEffect(() => {
    const root = window.document.documentElement
    if (isDarkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    
    // 保存主題設定
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light')
  }, [isDarkMode])

  // 監聽系統主題變更
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e) => {
      // 只有在沒有用戶自定義主題時才跟隨系統主題
      const savedTheme = localStorage.getItem('theme')
      if (!savedTheme) {
        setIsDarkMode(e.matches)
      }
    }
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev)
  }

  const setTheme = (theme) => {
    setIsDarkMode(theme === 'dark')
  }

  const value = {
    isDarkMode,
    toggleTheme,
    setTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}
